import { XIcon } from "lucide-react";

import { Row, Text } from "@/components";
import { Button } from "@/components/ui/button";

import { formatting } from "@/lib/formatting";
import { locations } from "@/lib/locations";
import { PipelineFilterAndSortState } from "@/types";

interface Pill {
  key: string;
  label: string;
  clear: () => void;
}

/**
 * Removable inline pills surfacing every active filter from the popover
 */
export const JobPipelineFilterPills = ({
  setIncludeCouldNotContact,
  setIncludeEmploymentEnded,
  setIncludeRejected,
  setLocations,
  setMaxCommuteSeconds,
  setSources,
  setStevieCallCompleted,
  state,
}: {
  setIncludeCouldNotContact: (next: boolean) => void;
  setIncludeEmploymentEnded: (next: boolean) => void;
  setIncludeRejected: (next: boolean) => void;
  setLocations: (next: string[]) => void;
  setMaxCommuteSeconds: (next: number | null) => void;
  setSources: (next: PipelineFilterAndSortState["sources"]) => void;
  setStevieCallCompleted: (next: boolean) => void;
  state: PipelineFilterAndSortState;
}) => {
  const pills = buildPills({
    state,
    setIncludeCouldNotContact,
    setIncludeEmploymentEnded,
    setIncludeRejected,
    setLocations,
    setMaxCommuteSeconds,
    setSources,
    setStevieCallCompleted,
  });
  if (pills.length === 0) return null;
  return (
    <Row className="items-center gap-1.5">
      {pills.map((p) => (
        <Row
          key={p.key}
          className="h-9 max-w-[260px] shrink-0 items-center gap-1 rounded-full border border-primary-400 bg-primary-50 px-3"
        >
          <Text className="truncate text-xs font-medium text-primary-900">
            {p.label}
          </Text>
          <Button
            aria-label={`Clear ${p.label}`}
            className="h-5 w-5 shrink-0 rounded-full p-0 text-primary-700 hover:bg-primary-100"
            onClick={p.clear}
            size="icon"
            variant="ghost"
          >
            <XIcon className="h-3 w-3" />
          </Button>
        </Row>
      ))}
    </Row>
  );
};

const buildPills = ({
  setIncludeCouldNotContact,
  setIncludeEmploymentEnded,
  setIncludeRejected,
  setLocations,
  setMaxCommuteSeconds,
  setSources,
  setStevieCallCompleted,
  state,
}: {
  setIncludeCouldNotContact: (next: boolean) => void;
  setIncludeEmploymentEnded: (next: boolean) => void;
  setIncludeRejected: (next: boolean) => void;
  setLocations: (next: string[]) => void;
  setMaxCommuteSeconds: (next: number | null) => void;
  setSources: (next: PipelineFilterAndSortState["sources"]) => void;
  setStevieCallCompleted: (next: boolean) => void;
  state: PipelineFilterAndSortState;
}): Pill[] => {
  const pills: Pill[] = [];
  if (state.sources.length > 0) {
    const labels = state.sources
      .map((s) => formatting.candidacySourceLabel(s))
      .join(", ");
    pills.push({
      key: "sources",
      label: `Source: ${labels}`,
      clear: () => {
        setSources([]);
      },
    });
  }
  if (state.locations.length > 0) {
    pills.push({
      key: "locations",
      label: `Location: ${state.locations.join(", ")}`,
      clear: () => {
        setLocations([]);
      },
    });
  }
  if (state.stevieCallCompleted) {
    pills.push({
      key: "stevieCallCompleted",
      label: "Stevie Call Completed",
      clear: () => {
        setStevieCallCompleted(false);
      },
    });
  }
  if (state.maxCommuteSeconds !== null) {
    pills.push({
      key: "maxCommuteSeconds",
      label: `Commute ≤ ${locations.formatCommuteDuration(state.maxCommuteSeconds)}`,
      clear: () => {
        setMaxCommuteSeconds(null);
      },
    });
  }
  if (state.includeRejected) {
    pills.push({
      key: "includeRejected",
      label: "Including Rejected",
      clear: () => {
        setIncludeRejected(false);
      },
    });
  }
  if (state.includeCouldNotContact) {
    pills.push({
      key: "includeCouldNotContact",
      label: "Including Could Not Contact",
      clear: () => {
        setIncludeCouldNotContact(false);
      },
    });
  }
  if (state.includeEmploymentEnded) {
    pills.push({
      key: "includeEmploymentEnded",
      label: "Including Employment Ended",
      clear: () => {
        setIncludeEmploymentEnded(false);
      },
    });
  }
  return pills;
};
